/**
 * ListGoogleMap Component
 * 
 * A comprehensive Google Maps component for displaying farm locations with interactive features.
 * Supports both desktop and mobile layouts with adaptive card positioning and boundary detection.
 * 
 * @author FarmCiti Team
 * @version 1.0.0
 */

import React, { useState, useEffect, useCallback, useRef} from "react";
import { useNavigate } from "react-router-dom";
import MediaQuery from 'react-responsive';
import {
  useJsApiLoader,
  GoogleMap,
  OverlayViewF,
  OVERLAY_MOUSE_TARGET
} from "@react-google-maps/api";
import { customStyle } from "./style/CustomStyle.js";
import GoogleMapStyle from "../../../assets/stylesheets/map/GoogleMap.module.css";
import MapCard from "../../farm/MapCard.jsx";
import ZoomControls from "./ZoomControls.jsx"
import logger from "../../../utils/logger.js";
// import {MapLabelLogo} from "../../../assets/logo/MapLabelLogo.jsx"
import slugify from 'slugify';

const log = logger.create('GoogleMap');

/**
 * Debounce utility function to limit the rate of function execution
 * 
 * @param {Function} func - The function to debounce
 * @param {number} wait - The number of milliseconds to delay
 * @returns {Function} - The debounced function
 */
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Calculates the optimal position offset for map cards with boundary detection
 * 
 * This function determines where to position the farm information card relative to
 * its marker, ensuring the card stays within map boundaries. Cards are positioned
 * above or below markers based on their location relative to the map center.
 * 
 * @param {Object} markerPosition - The lat/lng position of the marker
 * @param {Object} mapCenter - The Google Maps center object with lat/lng methods
 * @param {Object} map - The Google Maps instance
 * @returns {Object} - Object with x and y offset values in pixels
 */
const calculateCardOffsetWithBoundaries = (markerPosition, mapCenter, map) => {
  // Validate required parameters
  if (!mapCenter || typeof mapCenter.lat !== 'function' || !map) {
    return { x: 0, y: 0 };
  }
  
  // Configuration constants
  const CARD_OFFSET_PIXELS = 165;   // Distance from marker to card

  // Determine if marker is in top or bottom half of map
  const isTop = markerPosition.lat > mapCenter.lat();
  
  // Calculate initial offset - cards appear below markers in top half, above in bottom half
  const offsetX = 0; // Cards are horizontally centered on markers
  let offsetY = isTop ? CARD_OFFSET_PIXELS : -CARD_OFFSET_PIXELS;
  
  // Perform boundary detection and adjustment
  try {
    // Get map container dimensions
    const mapWidth = map.getDiv().offsetWidth;
    const mapHeight = map.getDiv().offsetHeight;
    
    // Get map projection for coordinate conversion
    const projection = map.getProjection();
    if (!projection) return { x: offsetX, y: offsetY };
    
    // Convert marker lat/lng to pixel coordinates
    const position = new window.google.maps.LatLng(markerPosition.lat, markerPosition.lng);
    const point = projection.fromLatLngToPoint(position);
    const scale = Math.pow(2, map.getZoom());
    const centerPoint = projection.fromLatLngToPoint(map.getCenter());
    
    // Calculate marker's pixel position on screen
    const pixelX = (point.x - centerPoint.x) * scale + mapWidth / 2;
    const pixelY = (point.y - centerPoint.y) * scale + mapHeight / 2;
    
    // Estimate card dimensions (you can adjust these)
    const ESTIMATED_CARD_WIDTH = 300;
    const ESTIMATED_CARD_HEIGHT = 200;
    const BORDER_PADDING = 10;
    
    // Adjust horizontal position to prevent card from going off screen edges
    let adjustedOffsetX = offsetX;
    if (pixelX + adjustedOffsetX - ESTIMATED_CARD_WIDTH/2 < 0) {
      // Card would extend beyond left edge - push it right
      adjustedOffsetX = BORDER_PADDING - pixelX + ESTIMATED_CARD_WIDTH/2;
    } else if (pixelX + adjustedOffsetX + ESTIMATED_CARD_WIDTH/2 > mapWidth) {
      // Card would extend beyond right edge - push it left
      adjustedOffsetX = mapWidth - pixelX - ESTIMATED_CARD_WIDTH/2 - BORDER_PADDING;
    }
    
    // Adjust vertical position to prevent card from going off screen edges
    let adjustedOffsetY = offsetY;
    if (pixelY + adjustedOffsetY - ESTIMATED_CARD_HEIGHT/2 < 0) {
      // Card would extend beyond top edge - push it down
      adjustedOffsetY = BORDER_PADDING - pixelY + ESTIMATED_CARD_HEIGHT/2;
    } else if (pixelY + adjustedOffsetY + ESTIMATED_CARD_HEIGHT/2 > mapHeight) {
      // Card would extend beyond bottom edge - push it up
      adjustedOffsetY = mapHeight - pixelY - ESTIMATED_CARD_HEIGHT/2 - BORDER_PADDING;
    }
    
    return { x: adjustedOffsetX, y: adjustedOffsetY };
    
  } catch (error) {
    // Fall back to basic offset if boundary calculation fails
    log.error('Error calculating boundary-aware offset:', error);
    return { x: offsetX, y: offsetY };
  }
};



function ActiveLabel({ label }) {
  // 선택된 라벨은 애니메이션 없이 항상 active 스타일만 적용
  // 리렌더링 방지: React.memo 사용
  return (
    <div
      className={[
        GoogleMapStyle["map-label"],
        GoogleMapStyle["active"],
      ].join(" ")}
      // 클릭/더블클릭 이벤트 제거 (고정)
      tabIndex={-1}
      aria-selected="true"
      onDoubleClick={e => { e.stopPropagation(); e.preventDefault(); }}

    >
      {label}
    </div>
  );
}
const MemoActiveLabel = React.memo(ActiveLabel);

function HighlightedLabel({ label, onClick }) {
  const [animClass, setAnimClass] = useState("highlight");
  useEffect(() => {
    const timer = setTimeout(() => setAnimClass(""), 200);
    return () => clearTimeout(timer);
  }, []);
  return (
    <div
      className={[
        GoogleMapStyle["map-label"],
        animClass && GoogleMapStyle[animClass],
        !animClass && GoogleMapStyle["active"],
      ].filter(Boolean).join(" ")}
      onClick={e => { e.stopPropagation(); onClick(); }}
      onDoubleClick={e => { e.stopPropagation(); e.preventDefault(); }}
    >
      {label}
    </div>
  );
}

/**
 * ListGoogleMapComponent - Main map component for displaying farm locations
 * 
 * Features:
 * - Interactive map with farm markers and information cards
 * - Responsive design (desktop/mobile layouts)
 * - Boundary-aware card positioning
 * - Debounced map change events for performance
 * - Zoom controls and user interaction tracking
 * 
 * @param {Array} markers - Array of marker objects with position and farm data
 * @param {Object} mapContainerStyle - Custom styles for map container
 * @param {string} className - Additional CSS classes
 * @param {Object} center - Initial map center coordinates {lat, lng}
 * @param {Function} onFarmSelect - Callback when a farm is selected
 * @param {Function} onMapBoundsChanged - Callback when map bounds change
 * @returns {JSX.Element} - Rendered map component
 */
export default function ListGoogleMapComponent({
  markers = [],
  mapContainerStyle = {},
  className = "",
  center = null,
  onFarmSelect = () => {},
  onMapBoundsChanged = () => {},
  highlightFarmId = null,
}) {
  // Initialize Google Maps API loader
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY
  });
  
  // Navigation hook for routing to farm detail pages
  const navigate = useNavigate();
  
  // State management for map functionality
  const [map, setMap] = useState(null);                             // Google Maps instance
  const [selectedFarm, setSelectedFarm] = useState(null);           // Currently selected farm marker
  const [mapCenter, setMapCenter] = useState(center);               // Current map center coordinates
  const [mapZoom, setMapZoom] = useState(9);                       // Current zoom level
  const [mapBounds, setMapBounds] = useState(null);                 // Current map boundaries
  const [isInitialLoad, setIsInitialLoad] = useState(true);         // Flag for initial map load
  const [userHasInteracted, setUserHasInteracted] = useState(false); // Track user interaction
  const [lastValidCenter, setLastValidCenter] = useState(null);     // Last valid center for duplicate prevention
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768); // Mobile/desktop detection
  const [cardOffset, setCardOffset] = useState({ x: 0, y: 0 });     // Calculated card position offset
  const [animClass, setAnimClass] = useState("");
  const [isMobileCardExiting, setIsMobileCardExiting] = useState(false);
  const [cardAnimClass, setCardAnimClass] = useState("exit");

  // Constants
  const COORDINATE_TOLERANCE = 0.000001; // Tolerance for detecting duplicate map positions

  // Default styling for map container
  const defaultStyle = {
    position: "relative",
    width: "100%",
    height: "100%",
    borderRadius: "12px",    
    overflow: "hidden"
  };

  /**
   * Debounced function to handle map changes (pan, zoom, etc.)
   * Prevents excessive API calls during user interaction
   */
  const debouncedMapChange = useCallback(
    debounce((mapInstance) => {
      // Only process map changes after initial load and user interaction
      if (!userHasInteracted || isInitialLoad) {
        return;
      }

      // Validate map instance before proceeding
      if (!mapInstance || typeof mapInstance.getCenter !== 'function') {
        log.warn('Map instance not available or invalid');
        return;
      }

      // Extract map properties safely
      let newCenter, newZoom, bounds;
      try {
        newCenter = mapInstance.getCenter();
        newZoom = mapInstance.getZoom();
        bounds = mapInstance.getBounds();
      } catch (error) {
        log.error('Error getting map properties:', error);
        return;
      }

      // Validate extracted map center
      if (!newCenter || typeof newCenter.lat !== 'function' || typeof newCenter.lng !== 'function') {
        log.warn('Invalid map center object');
        return;
      }

      // Validate extracted map bounds
      if (!bounds || typeof bounds.getNorthEast !== 'function') {
        log.warn('Invalid map bounds object');
        return;
      }

      // Convert center to plain object
      let centerData;
      try {
        centerData = {
          lat: newCenter.lat(),
          lng: newCenter.lng()
        };
      } catch (error) {
        log.error('Error extracting center coordinates:', error);
        return;
      }

      // Validate coordinate values are within valid ranges
      if (isNaN(centerData.lat) || isNaN(centerData.lng) || 
          Math.abs(centerData.lat) > 90 || Math.abs(centerData.lng) > 180) {
        log.warn('Invalid coordinates:', centerData);
        return;
      }

      // Prevent duplicate searches for the same location
      if (lastValidCenter && 
          Math.abs(lastValidCenter.lat - centerData.lat) < COORDINATE_TOLERANCE &&
          Math.abs(lastValidCenter.lng - centerData.lng) < COORDINATE_TOLERANCE) {
        return;
      }
      
      // Update component state with new map data
      setMapCenter(centerData);
      setMapZoom(newZoom);
      setLastValidCenter(centerData);
      
      // Convert bounds to plain object
      let boundsData;
      try {
        boundsData = {
          north: bounds.getNorthEast().lat(),
          south: bounds.getSouthWest().lat(),
          east: bounds.getNorthEast().lng(),
          west: bounds.getSouthWest().lng()
        };
      } catch (error) {
        log.error('Error extracting bounds coordinates:', error);
        return;
      }
      
      // Update bounds state
      setMapBounds(boundsData);

      // Notify parent component of map changes
      try {
        onMapBoundsChanged({
          center: centerData,
          zoom: newZoom,
          bounds: boundsData
        });
      } catch (error) {
        log.error('Error calling onMapBoundsChanged:', error);
      }
    }, 500), // 500ms debounce delay
    [userHasInteracted, isInitialLoad, lastValidCenter, onMapBoundsChanged]
  );

  const handleCardClose = () => {
    setCardAnimClass("exit");

  };

  // close card when the user clicks the close button
  const handleMapClick = () => {
    if (selectedFarm) {
      setSelectedFarm(null);
      onFarmSelect(null);
    }
  };

  // show card when a farm is selected
  const handleLabelClick = (marker) => {
    if (!map) return;
    if (selectedFarm?.id === marker.id) return;

    setCardOffset(
      calculateCardOffsetWithBoundaries(marker.position, map.getCenter(), map)
    );
    setSelectedFarm(marker);
    setCardAnimClass("enter");
    onFarmSelect(marker);
  };

  /**
   * Map card closed when the user click the map
  */
  const handleMobileClose = () => {
    setIsMobileCardExiting(true);

    setTimeout(() => {
      setSelectedFarm(null);
      onFarmSelect(null);
      setIsMobileCardExiting(false);
    }, 650);
  };

  /**
   * Effect: Handle initial map positioning and setup
   * Runs once when the map loads and markers are available
   */
  useEffect(() => {
    if (!map || !isInitialLoad) return;
    
    const timer = setTimeout(() => {
      try {
        // Trigger map resize to ensure proper rendering
        window.google.maps.event.trigger(map, "resize");

        // Skip repositioning if user has already interacted with the map
        if (userHasInteracted) return;

        // Position map based on available data
        if (center) {
          // Use provided center coordinates
          map.setCenter(center);
          map.setZoom(9);
          setLastValidCenter(center);
        } else if (markers.length === 1) {
          // Center on single marker
          map.panTo(markers[0].position);
          map.setZoom(9);
          setLastValidCenter(markers[0].position);
        } else if (markers.length > 1) {
          // Fit map to show all markers
          const bounds = new window.google.maps.LatLngBounds();
          markers.forEach((m) => bounds.extend(m.position));
          map.fitBounds(bounds);
        }
        
        // Mark initial load as complete after positioning
        setTimeout(() => {
          setIsInitialLoad(false);
        }, 1000);
      } catch (error) {
        setIsInitialLoad(false);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [map, markers, center, isInitialLoad, userHasInteracted]);

  /**
   * Effect: Handle window resize events for mobile/desktop detection
   */
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /**
   * Effect: Notify parent component when farm selection changes
   */
  useEffect(() => {
    if (selectedFarm) {
      onFarmSelect(selectedFarm);
    }
  }, [selectedFarm]);




  // Handle loading states
  if (loadError) return <div>Map failed to load</div>;
  if (!isLoaded) return <div>Loading…</div>;
  
  return (
    <GoogleMap
      mapContainerStyle={{ ...defaultStyle, ...mapContainerStyle }}
      className={className}
      onClick={handleMapClick}
      onLoad={(mapInstance) => {
        // Store map instance reference
        setMap(mapInstance);
        
        // Set initial map position based on available data
        if (center) {
          mapInstance.setCenter({
            lat: center.lat,
            lng: center.lng
          });
          mapInstance.setZoom(9);
        } else if (markers.length === 1) {
          mapInstance.setCenter(markers[0].position);
          mapInstance.setZoom(9);
        } else if (markers.length > 1) {
          const bounds = new window.google.maps.LatLngBounds();
          markers.forEach((m) => bounds.extend(m.position));
          mapInstance.fitBounds(bounds);
        }
        
        // Set up bounds change listener for initial bounds capture
        const boundsListener = window.google.maps.event.addListener(mapInstance, 'bounds_changed', () => {
          try {
            const bounds = mapInstance.getBounds();
            if (bounds) {
              const boundsData = {
                north: bounds.getNorthEast().lat(),
                south: bounds.getSouthWest().lat(),
                east: bounds.getNorthEast().lng(),
                west: bounds.getSouthWest().lng()
              };
              setMapBounds(boundsData);
              
              // Remove listener after first successful bounds setting
              window.google.maps.event.removeListener(boundsListener);
            }
          } catch (error) {
            log.error('Error setting initial map bounds:', error);
          }
        });
      }}
      onIdle={() => {
        // Handle map idle events (after panning/zooming stops)
        if (!isInitialLoad && map && typeof map.getCenter === 'function') {
          setUserHasInteracted(true);
          debouncedMapChange(map);
        }
      }}
      onDragStart={() => {
        // Track when user starts dragging the map
        if (!isInitialLoad) {
          setUserHasInteracted(true);
        }
      }}
      onZoomChanged={() => {
        // Track when user changes zoom level
        if (!isInitialLoad) {
          setUserHasInteracted(true);
        }
      }}

      options={{
        disableDefaultUI: true,    // Hide default map controls
        zoomControl: false,        // Disable default zoom controls (we use custom ones)
        scrollwheel: true,         // Allow mouse wheel zooming
        gestureHandling: "greedy", // Allow immediate gesture handling
        styles: customStyle,       // Apply custom map styling
        clickableIcons: false,     // Disable clicking on map POI icons
      }}
    >
      {/* Render farm markers and cards */}
      {markers.map((marker) => {
        const isActive = selectedFarm?.id === marker.id;
        const isHighlighted = !isActive && highlightFarmId === marker.id;

        // 깜빡임의 근본 원인: OverlayViewF의 key가 marker.id로 고정되어도,
        // highlightFarmId가 바뀔 때 부모 컴포넌트가 리렌더링되면서
        // 선택된 라벨의 OverlayViewF 내부가 다시 렌더링됨.
        // 이때 선택된 라벨의 OverlayViewF와 MapCard가 리렌더링되면
        // CSS 애니메이션이 다시 트리거되어 깜빡임이 발생.

        // 해결 방법:
        // 1. 선택된 라벨의 OverlayViewF와 MapCard를 React.memo로 감싸서
        //    highlightFarmId가 바뀌어도 리렌더링되지 않게 한다.
        // 2. 선택된 라벨의 OverlayViewF key를 marker.id + '-selected'로 고정하여
        //    highlightFarmId가 바뀌어도 절대 key가 바뀌지 않게 한다.

        // 아래처럼 선택된 라벨만 별도의 key와 memoized 컴포넌트로 분리:
        if (isActive) {
          return (
            <React.Fragment key={marker.id + '-selected'}>
              <MemoActiveOverlay
                marker={marker}
                cardOffset={cardOffset}
                cardAnimClass={cardAnimClass}
                selectedFarm={selectedFarm}
                navigate={navigate}
                handleCardClose={handleCardClose}
                isMobileCardExiting={isMobileCardExiting}
                handleMobileClose={handleMobileClose}
                // highlightFarmId 등 외부 상태 절대 넘기지 않음!
              />
            </React.Fragment>
          );
        }

        // 나머지 라벨은 기존대로
        return (
          <React.Fragment key={marker.id}>
            <OverlayViewF
              position={marker.position}
              mapPaneName={OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={(w = 0, h = 0) => ({
                x: -w / 2,
                y: -h / 2
              })}
            >
              {isHighlighted ? (
                <HighlightedLabel
                  label={marker.label}
                  onClick={() => handleLabelClick(marker)}
                />
              ) : (
                <div
                  className={GoogleMapStyle["map-label"]}
                  onClick={e => { e.stopPropagation(); handleLabelClick(marker); }}
                  onDoubleClick={e => { e.stopPropagation(); e.preventDefault(); }}
                >
                  {marker.label}
                </div>
              )}
            </OverlayViewF>
          </React.Fragment>
        );
      })}

      {/* Custom Zoom Controls */}
      {map && <ZoomControls map={map} />}
    </GoogleMap>
  );
}

// 선택된 라벨과 카드 전체를 memo로 감싸되, props로 highlightFarmId를 절대 넘기지 않음
const MemoActiveOverlay = React.memo(function MemoActiveOverlay({
  marker,
  cardOffset,
  cardAnimClass,
  selectedFarm,
  navigate,
  handleCardClose,
  isMobileCardExiting,
  handleMobileClose,
}) {
  // highlightFarmId 등 외부 상태와 무관하게 항상 고정 렌더링
  return (
    <>
      <OverlayViewF
        position={marker.position}
        mapPaneName={OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={(w = 0, h = 0) => ({
          x: -w / 2,
          y: -h / 2
        })}
      >
          <div onClick={e => e.stopPropagation()}>
            <MemoActiveLabel label={marker.label} />
          </div>
      </OverlayViewF>
      {/* Farm Information Card (shown when marker is selected) */}
      <>
        <MediaQuery minWidth={768}>
          <OverlayViewF
            position={marker.position}
            mapPaneName={OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={(w, h) => {
              return { x: cardOffset.x - w/2, y: cardOffset.y - h/2 };
            }}
          >
            <div
              className={[
                GoogleMapStyle["map-card-overlay"],
                GoogleMapStyle[
                  selectedFarm?.id === marker.id
                    ? cardAnimClass
                    : "exit"
                ]
              ].join(" ")}
              style={{
                position: 'relative',
                zIndex: 1000,
                maxWidth: '320px',
                minWidth: '280px',
              }}
            >
              {selectedFarm?.id === marker.id && (
                <MapCard
                  farm={selectedFarm.farm}
                  onClick={() => {
                    const slug = slugify(marker.farm.name || '', { lower: true, strict: true });
                    navigate(`/farm/${marker.id}/${slug}`);
                  }}
                  onClose={handleCardClose}
                />
              )}
            </div>
          </OverlayViewF>
        </MediaQuery>
        <MediaQuery maxWidth={767}>
          <div
            className={[
              GoogleMapStyle["mobile-card-container"],
              isMobileCardExiting
                ? GoogleMapStyle["slideDown"]
                : GoogleMapStyle["slideUp"]
            ].join(" ")}
          >
            <MapCard
              farm={selectedFarm.farm}
              onClick={() =>{
                const slug = slugify(marker.farm.name || '', { lower: true, strict: true });
                navigate(`/farm/${marker.id}/${slug}`)}}
              onClose={handleMobileClose}
            />
          </div>
        </MediaQuery>
      </>
    </>
  );
}, (prevProps, nextProps) => {
  // highlightFarmId 등 외부 상태가 바뀌어도 리렌더링 방지
  return (
    prevProps.marker.id === nextProps.marker.id &&
    prevProps.selectedFarm?.id === nextProps.selectedFarm?.id &&
    prevProps.cardOffset.x === nextProps.cardOffset.x &&
    prevProps.cardOffset.y === nextProps.cardOffset.y &&
    prevProps.cardAnimClass === nextProps.cardAnimClass &&
    prevProps.isMobileCardExiting === nextProps.isMobileCardExiting
    // highlightFarmId 등 외부 상태는 비교하지 않음!
  );
});
